//#region Util Imports

import * as moment from 'moment'
import * as _ from 'underscore'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'
import 'devextreme/ui/button'
import 'devextreme/viz/chart'
import 'devextreme/viz/pie_chart'
import { chartAxisObject, chartPointAggregationInfoObject } from 'devextreme/viz/chart'
import { VizRange } from 'devextreme/viz/common'

//#endregion

import { i18n } from '../../i18n/common.i18n'
import { MeasurementDataType } from '../measurements.controller'
import { Enums } from '../../../../models/common/enums'
import { LocalDictionaryFactory } from '../../../api/api.module'
import { MeasurementModel, DataTypeFormatGroupModel } from '../../../api/api.models'

//#region Service Imports

import { DataTypeFormatService, DataTypeFormatter } from '../../../../services/data-type'
import { UserSessionService } from '../../../../services/user-session'

//#endregion

//#region Helper Imports

import { OperatorLayoutSettings } from '../../../../helpers/operator-layout-settings'
import { EventsNames } from '../../../../helpers/events-names'
import { CommonHelper, PieChartType, ChartType } from '../../../../helpers/common-helper'
import { ConfigHelper } from '../../../../helpers/config-helper'
import { Permissions } from '../../../../helpers/permissions'
import { LocalizationHelper, TemplateHelper } from '../../../../helpers'
import { Theme, ThemeHelper } from '../../../../helpers/theme-helper'

//#endregion

import { IMRBaseControlDirectiveController } from '../../base'

export interface MeasurementsChartBridge {
    data: any[]
    filterId: number
    selectedTypes: MeasurementDataType[]
    tickInterval: any
    dataTypesChartStorageName?: string
    updateChart?: boolean
    clearChart?: boolean
    thresholdTypes: any[]
    labelFormat?: any
    isKPI?: boolean

    chartOptionsPermissions: any[]         // TODO: Remove in future, use showChartOptions
    chartDataTypeOptionsPermissions: any[] // TODO: Remove in future, use showDataTypePanel

    hideChartOptions?: boolean
    hideDataTypePanel?: boolean
    useAggregation?: boolean
}

interface VisibleType {
    id: number,
    state: boolean,
    axisName: string,
    seriesName: string
}

interface MeasurementsChartScope extends ng.IScope {
    chartOptions: DevExpress.viz.dxChartOptions
    noSeriesSelected: boolean
    isLegendVisible: boolean
    areTooltipsShared: boolean
    isRotated: boolean
    source: MeasurementsChartBridge

    devices?: any[]
    meters?: any[]
    groups?: any[]

    showAllSeries: boolean
    isChartFullscreen: boolean
    resizeChart: () => void

    tooltipButtonOptions: DevExpress.ui.dxButtonOptions
    legendButtonOptions: DevExpress.ui.dxButtonOptions
    rotateButtonOptions: DevExpress.ui.dxButtonOptions
    resizeButtonOptions: DevExpress.ui.dxButtonOptions

    chartButtonsHidden: boolean
    isLineChart: boolean
}

export class ChartDirectiveController extends IMRBaseControlDirectiveController {

    // Consts

    private LOG_STRING = '[Chart.Controller] - %s'

    // Consts End


    private static get AlternativeColors(): string[] { return ['#5F8B95', '#BA4D51', '#D7C5A9', '#CAAFB8', '#C2CBB3', '#BFB4C6', '#91BDC7'] }

    chart: DevExpress.viz.dxChart
    chartDeferred: ng.IDeferred<DevExpress.viz.dxChart>

    selectedTypes: MeasurementDataType[]
    visibleTypes: VisibleType[]
    data: any[]
    chartStorageName: string
    showAllSeries: boolean

    tooltipButton: DevExpress.ui.dxButton
    legendButton: DevExpress.ui.dxButton
    rotateButton: DevExpress.ui.dxButton
    resizeButton: DevExpress.ui.dxButton
    colors: any[]
    onLegendClick: boolean = false
    private dateTimeFormat: string;
    private unitedAxes: any

    private dataTypeFormatter: DataTypeFormatter
    private legendError = false

    constructor(
        private $scope: MeasurementsChartScope,
        private $element: JQuery,
        private $filter: ng.IFilterService,
        private $q: ng.IQService,
        private $timeout: ng.ITimeoutService,
        private localDictionary: LocalDictionaryFactory,
        private dataTypeFormatService: DataTypeFormatService,
        private userSession: UserSessionService
    ) {
        super()

        this.chartDeferred = $q.defer()

        var dataExists = false
        var parent = $element.parent()

        this.$scope.isLineChart = true

        this.dateTimeFormat = $scope.source.isKPI ? window['simax'].getDateFormat('DATE_FORMAT_MOMENTJS') : window['simax'].getDateFormat('DATE_TIME_FORMAT_MOMENTJS');

        $scope.noSeriesSelected = true
        $scope.isLegendVisible = OperatorLayoutSettings.getSetting(OperatorLayoutSettings.IsLegendVisible, false)
        $scope.isRotated = OperatorLayoutSettings.getSetting(OperatorLayoutSettings.IsRotated, false)
        $scope.areTooltipsShared = OperatorLayoutSettings.getSetting(OperatorLayoutSettings.SharedTooltips, false)
        $scope.isChartFullscreen = false
        $scope.chartButtonsHidden = true

        $scope.$watch(() => {
            if (this.visibleTypes) return this.visibleTypes.every(vt => !vt.state)
            else return false
        }, noSeriesSelected => {
            $scope.noSeriesSelected = noSeriesSelected
        })

        $scope.$on(EventsNames.UPDATE_CHART, () => {
            if (this.chart) {
                this.resetChartSize()
            }
            this.refreshChart()
        })

        $scope.$watchCollection("source", (source: MeasurementsChartBridge) => {
            if (source && source.selectedTypes.length > 0) {
                const idDataTypes: Enums.DataType[] = _.map(source.selectedTypes, (type: MeasurementDataType) => {
                    return type.dataTypeId
                })

                this.dataTypeFormatService
                    .getDataTypeFormatter(idDataTypes)
                    .then((dataTypeFormatter: DataTypeFormatter) => {
                        this.dataTypeFormatter = dataTypeFormatter

                        const firstSerieChartType = source.selectedTypes[0].chartType;

                        let isLineChart = true;
                        for (let key in PieChartType) {
                            if (PieChartType[key] == firstSerieChartType || key.toLowerCase() == firstSerieChartType.toLowerCase())
                                isLineChart = false;
                        }

                        this.$scope.isLineChart = isLineChart;
                        this.isAggregationEnabled = isLineChart && source.useAggregation;

                        const selectedTypes = source.selectedTypes.filter(selectedType => {
                            const isTypeInPieCharts = Object.keys(PieChartType).filter(key => key.toLowerCase() == selectedType.chartType.toLowerCase()).length > 0 ? true : false;

                            if ($scope.isLineChart) {
                                if (isTypeInPieCharts) return false;
                                else {
                                    return Object.keys(ChartType).filter(key => key.toLowerCase() == selectedType.chartType.toLowerCase()).length > 0 ? true : false;
                                }
                            }
                            else {
                                return isTypeInPieCharts;
                            }
                        })

                        let data = source.data;
                        data = data.filter(d => {
                            for (let i = 0; i < selectedTypes.length; i++) {
                                if (selectedTypes[i].id == d.dataType)
                                    return true;
                            }
                            return false;
                        })

                        if (!this.$scope.isLineChart) {
                            let newestData = [];
                            selectedTypes.forEach(type => {
                                let newest: any = 0;
                                source.data.forEach(data => {
                                    if (data.dataType == type.id && data.date > newest)
                                        newest = data;
                                })
                                if (newest != 0) newestData.push(newest)
                            })
                            data = newestData;
                        }

                        this.chartDeferred.promise.then(chart => {
                            if (this.chart && source.data) {
                                this.resetChartSize()
                                this.updateChart(data, selectedTypes, source.filterId, $scope.source.tickInterval, $scope.groups, $scope.devices, $scope.meters, $scope.source.labelFormat, source.dataTypesChartStorageName)
                            }
                            if (source.updateChart) {
                                $timeout(() => {
                                    this.resetChartSize()
                                    this.refreshChart()
                                }, 1000)
                                source.updateChart = false
                            }
                            if (source.clearChart) {
                                $timeout(() => {
                                    if (this.chart) {
                                        this.chart.option('dataSource', [])
                                    }
                                    this.refreshChart()
                                }, 0)
                                source.clearChart = false
                            }
                        })
                    })
            } else {
                if (this.chart && source) {
                    this.chart.option('dataSource', [])
                    this.updateChart(source.data, source.selectedTypes, source.filterId, $scope.source.tickInterval, $scope.groups, $scope.devices, $scope.meters, $scope.source.labelFormat, source.dataTypesChartStorageName)
                }
                $timeout(() => {
                    this.resetChartSize()
                    this.refreshChart()
                }, 1000)
            }
        })

        //close fullscreen chart on ESC press
        $(document).keyup((e) => {
            if (e.keyCode == 27) {
                if (this.$scope.isChartFullscreen) {
                    $timeout(() => {
                        this.resizeChart(parent)
                    })
                }
            }
        })

        const theme: Theme = ThemeHelper.getTheme(userSession)


        var onLegendClickTimeout
        $scope.chartOptions = {
            onInitialized: e => {
                this.chart = e.component
                this.chartDeferred.resolve(this.chart)

                if ($scope.source) {
                    if ($scope.source.hideChartOptions != null) {
                        $scope.chartButtonsHidden = $scope.source.hideChartOptions
                        if ($scope.source.hideChartOptions) {
                            $scope.isLegendVisible = true
                            $scope.isRotated = false
                            $scope.areTooltipsShared = true
                            $timeout(() => {
                                this.chart.option('legend.visible', $scope.isLegendVisible)
                                this.chart.option('tooltip.shared', $scope.areTooltipsShared)
                            })
                        }
                    } else if ($scope.source.chartOptionsPermissions) {
                        Permissions.hasPermissions($scope.source.chartOptionsPermissions).then(permision => {
                            $scope.chartButtonsHidden = permision
                            if (permision) {
                                $scope.isLegendVisible = true
                                $scope.isRotated = false
                                $scope.areTooltipsShared = true
                                $timeout(() => {
                                    this.chart.option('legend.visible', $scope.isLegendVisible)
                                    this.chart.option('tooltip.shared', $scope.areTooltipsShared)
                                })
                            }
                        })
                    }
                }
                //this.updateChart($scope.source.data, $scope.source.selectedTypes, $scope.source.filterId, $scope.source.tickInterval, $scope.groups, $scope.devices, $scope.meters, $scope.source.labelFormat, $scope.source.dataTypesChartStorageName)
            },
            synchronizeMultiAxes: false,
            customizePoint: (point: any) => {
                if (!$scope.isLineChart && point.color) {
                    return { color: point.color }
                }
                else return undefined;
            },
            resolveLabelOverlapping: "stack",
            palette: 'Office',
            adaptiveLayout: {
                width: 0,
                height: 0
            },
            containerBackgroundColor: ConfigHelper.getValue(Enums.SimaxConfigSection.Charts, ConfigHelper.Key.BackgroundColorChart),
            bindingOptions: {
                rotated: 'isRotated',
                dataSource: 'dataSource',
            },
            onIncidentOccurred: (e: any) => {
                if (e.target.id == "W2104" && e.component._legend._size.height) {
                    e.component.option({
                        'height': e.component._legend._size.height + 400,
                        'width': e.component._legend._size.width + 400
                    })
                    this.legendError = true
                    this.$timeout(() => {
                        this.refreshChart()
                    })
                }
            },
            onSeriesClick: (e) => {
                if (this.onLegendClick) {
                    this.onLegendClick = false
                }
                else {
                    this.updateValueAxisWidth(e)
                }
            },

            onZoomEnd: (e) => {
                this.updateAggregationIntervalsDebounced(e.range)
            },

            scrollingMode: 'all',
            zoomingMode: 'all',
            commonAxisSettings: {
                tick: {
                    visible: false,
                },

                label: {
                    wordWrap: 'none'
                }
            },
            argumentAxis: {
                argumentType: 'datetime',
                axisDivisionFactor: 40,
                tickInterval: { hours: 2 },
                grid: {
                    visible: true
                },
                tick: {
                    visible: true,
                    length: 10,
                    color: 'blue',
                    width: 2
                },
                /*
                // formatowanie etykiet w zależności od trybu KPI / pomiary
                label: {
                    customizeText: (options) => {
                        return moment(options.value).format(this.dateTimeFormat)
                    }
                },
                */
                minValueMargin: 0.01,
                maxValueMargin: 0.01,
                valueMarginsEnabled: true
            },
            valueAxis: [
                {
                    name: "number",
                    valueType: "numeric"
                },
                {
                    name: "date",
                    valueType: "string",
                    label: {
                        customizeText: (options) => {
                            return moment(options.value).format(this.dateTimeFormat)
                        }
                    }
                }
            ],
            tooltip: {
                enabled: true,
                shared: $scope.areTooltipsShared,
                color: theme.defaultBackgroundColor,
                border: {
                    color: theme.defaultBorderColor
                },

                customizeTooltip: (point: any) => {
                    if ($scope.isLineChart) {
                        const idDataType = (<any>point.point.series.tag).dataTypeId

                        if (!$scope.areTooltipsShared) {
                            const dataTypes = localDictionary.getDataTypesSync([idDataType])
                            const pointObject: DevExpress.viz.chartPointObject = point.point
                            const unitName = (<any>pointObject.series.tag).unitName
                            const tag = point.point.tag
                            const color = point.point.getColor()
                            let value = this.isAggregationActive() ? point.value : point.valueText

                            if (typeof point.value === 'boolean') {
                                if (point.value)
                                    value = i18n("Common", "True")
                                else
                                    value = i18n("Common", "False")
                            }

                            let argument: string = null;

                            if (this.isAggregationActive()) {
                                const aggregationInfo: chartPointAggregationInfoObject = pointObject.aggregationInfo;
                                const fromText: string = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.From);
                                const toText: string = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.To);
                                const aggregationText: string = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Aggregation)
                                const fromDate: string = moment(aggregationInfo.intervalStart).format(this.dateTimeFormat);
                                const toDate: string = moment(aggregationInfo.intervalEnd).format(this.dateTimeFormat);

                                argument = `${aggregationText} ${fromText.toLowerCase()}: ${fromDate} ${toText.toLowerCase()}: ${toDate}`;
                            } else {
                                argument = moment(point.argument).format(this.dateTimeFormat);
                            }

                            if ((<any>pointObject.series.tag).isDate) {
                                value = moment(point.value).format(this.dateTimeFormat)
                            }

                            if (dataTypes[0].type == CommonHelper.boolean) {
                                var additionalClass = ''
                                if ((value > 0 || value < 0 || value.toString().toLowerCase() === 'true') && value != null)
                                    additionalClass = ' dx-checkbox-checked '
                                else if (value == null)
                                    additionalClass = ' dx-checkbox-indeterminate '
                                value = '<div class="dx-datagrid-checkbox-size dx-widget dx-state-readonly' + additionalClass + 'dx-checkbox" aria-readonly="true" role="checkbox" style="display:inline-block;" aria-checked="' + value + '"><input type="hidden" value="' + value + '" /><div class="dx-checkbox-container"><span class="dx-checkbox-icon"></span></div></div>'
                            } else if (this.isNumeric(value))
                                value = this.dataTypeFormatter.formatDisplayValue(+value, {
                                    idDataType: idDataType,
                                    idDataTypeClass: Enums.DataTypeClass.Integer
                                })
                            return {
                                html: "<strong><u><span style='color:" + theme.defaultFontColor + ";'>" + argument + "</span></u></strong><br/>" + (tag ? "<strong><span style='color:" + theme.defaultFontColor + ";'>" + tag + "</span></strong><br/>" : '') + "<strong><span style='color:" + color + ";'>" + "&nbsp&nbsp" + point.seriesName.replace('undefined: ', '') + ":</span> " + value + " " + this.encloseUnit(unitName) + "</strong>",
                            }
                        } else {
                            var html = '<div>';
                            let arg = "";
                            let tagMem = "";

                            point.points.forEach(pt => {
                                const pointObject: DevExpress.viz.chartPointObject = pt.point
                                const unitName = (<any>pointObject.series.tag).unitName
                                const dataTypes = localDictionary.getDataTypesSync([pt.point.series.tag.dataTypeId])
                                const tag = pointObject.tag
                                const color = pointObject.getColor()
                                let value = this.isAggregationActive() ? pt.value : pt.valueText

                                if (typeof pt.value === 'boolean') {
                                    if (pt.value === true)
                                        value = i18n("Common", "True")
                                    else
                                        value = i18n("Common", "False")
                                }

                                let argument: string = null;

                                if (this.isAggregationActive()) {
                                    const aggregationInfo: chartPointAggregationInfoObject = pointObject.aggregationInfo;
                                    const fromText: string = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.From);
                                    const toText: string = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.To);
                                    const aggregationText: string = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Aggregation)
                                    const fromDate: string = moment(aggregationInfo.intervalStart).format(this.dateTimeFormat);
                                    const toDate: string = moment(aggregationInfo.intervalEnd).format(this.dateTimeFormat);

                                    argument = `${aggregationText} ${fromText.toLowerCase()}: ${fromDate} ${toText.toLowerCase()}: ${toDate}`;
                                } else {
                                    argument = moment(pt.argument).format(this.dateTimeFormat);
                                }

                                if ((<any>pointObject.series.tag).isDate) {
                                    value = moment(pt.value).format(this.dateTimeFormat)
                                }
                                if (dataTypes[0].type == CommonHelper.boolean) {
                                    var additionalClass = ''
                                    if ((value > 0 || value < 0 || value.toString().toLowerCase() === 'true') && value != null)
                                        additionalClass = ' dx-checkbox-checked '
                                    else if (value == null)
                                        additionalClass = ' dx-checkbox-indeterminate '
                                    value = '<div class="dx-datagrid-checkbox-size dx-widget dx-state-readonly' + additionalClass + 'dx-checkbox" aria-readonly="true" role="checkbox" style="display:inline-block;" aria-checked="' + value + '"><input type="hidden" value="' + value + '" /><div class="dx-checkbox-container"><span class="dx-checkbox-icon"></span></div></div>'
                                } else if (this.isNumeric(value)) {
                                    value = this.dataTypeFormatter.formatDisplayValue(+value, {
                                        idDataType: idDataType,
                                        idDataTypeClass: Enums.DataTypeClass.Integer
                                    })
                                }

                                if (argument != arg) {
                                    arg = argument;
                                    html += "<strong><u><span style='color:" + theme.defaultFontColor + ";'>" + argument + "</span></u></strong><br/>";
                                }
                                if (tag != tagMem) {
                                    tagMem = tag;
                                    html += (tag ? "<strong><span style='color:" + theme.defaultFontColor + ";'>" + tag + "</span></strong><br/>" : '');
                                }
                                html += "<strong><span style='color:" + color + ";'>" + "&nbsp&nbsp" + pt.seriesName.replace('undefined: ', '') + ":</span> " + value + " " + this.encloseUnit(unitName) + "</strong><br/>"
                            })
                            html += '</div>'
                            return {
                                html: html
                            }
                        }
                    } else {
                        const type: any = this.selectedTypes.filter(type => type.id == point.argument)
                        return {
                            html: `<span style='color:"${theme.defaultFontColor}";'>${type[0].name} ${point.value} ${type.unitName ? type.unitName : ''} (${point.percentText})</span>`
                        }
                    }
                },
                zIndex: 10000
            },
            commonSeriesSettings: {
                tagField: "info",
                argumentField: "date",
                ignoreEmptyPoints: true,
                axis: "number",
                point: {
                    size: 4,
                    hoverStyle: {
                        size: 3
                    }
                }
            },
            dataPrepareSettings: {
                checkTypeForAllData: true
            },
            legend: {
                visible: $scope.isLegendVisible ? $scope.isLineChart : false,
                orientation: 'horizontal',
                horizontalAlignment: 'center',
                itemTextPosition: 'right',
                rowItemSpacing: 2,
                columnItemSpacing: 2,
                customizeText: (seriesInfo) => {
                    if (!this.chart || !seriesInfo) return ''

                    let text = null

                    if ($scope.isLineChart) {
                        var series = null
                        try {
                            series = this.chart.getSeriesByPos(seriesInfo.seriesIndex)
                        } catch (e) { }
                        if (series == null) return ''
                        var seriesTag: any = series.tag
                        text = seriesInfo.seriesName.replace('undefined: ', '').substring(0, 20) + " " + this.encloseUnit(seriesTag.unitName)
                        return text
                    } else {
                        try {
                            const allSeries: DevExpress.viz.baseSeriesObject[] = this.chart.getAllSeries()

                            if (allSeries && allSeries.length > 0) {
                                const currentSeries: DevExpress.viz.baseSeriesObject = allSeries[0]

                                if (currentSeries) {
                                    const pointSeries: any = <any>seriesInfo

                                    if (pointSeries && pointSeries.pointIndex != null && pointSeries.pointName != null) {
                                        const point: any = currentSeries.getPointByPos(pointSeries.pointIndex)
                                        let referenceObjString: string = null
                                        let unitName: string = null

                                        if (point && point.tag && point.tag !== undefined && point.tag !== null) {
                                            referenceObjString = point.tag.toString()
                                        }

                                        if (typeof (pointSeries.pointName) === 'string') {
                                            const pointNameSplit: string[] = pointSeries.pointName.split('-')

                                            if (pointNameSplit && pointNameSplit.length > 1) {
                                                const idDataTypePointStr: string = pointNameSplit[0]
                                                const selectedDataTypes: MeasurementDataType[] = this.selectedTypes.filter((x: MeasurementDataType) => { return x.dataTypeId === +idDataTypePointStr })

                                                if (selectedDataTypes && selectedDataTypes.length > 0) {
                                                    unitName = selectedDataTypes[0].unitName
                                                }

                                                if (referenceObjString == null) {
                                                    referenceObjString = pointNameSplit[1]
                                                }

                                                text = referenceObjString
                                            }
                                        }
                                        else {
                                            const selectedDataType: MeasurementDataType = this.selectedTypes[0]

                                            if (selectedDataType != null) {
                                                unitName = selectedDataType.unitName
                                            }

                                            if (referenceObjString == null) {
                                                referenceObjString = pointSeries.pointName
                                            }

                                            text = referenceObjString
                                        }

                                        if (text != null && unitName != null) {
                                            text = text.substring(0, 20) + " " + this.encloseUnit(unitName)
                                        }
                                        else {
                                            console.warn(this.LOG_STRING, 'Unable to set unit of the current series.')
                                        }
                                    }
                                }
                            }

                            // Last fallback
                            if (!text) {
                                console.info(this.LOG_STRING, 'Unable to find series text. Using the fallback method.')

                                const currentSeries = <any>seriesInfo

                                if (currentSeries && currentSeries.pointName) {
                                    if (typeof (currentSeries.pointName) === 'string') {
                                        const pointNameSplit: string[] = currentSeries.pointName.split('-')

                                        if (pointNameSplit && pointNameSplit.length > 1) {
                                            text = pointNameSplit[1]
                                        }
                                    }
                                    else {
                                        text = currentSeries.pointName
                                    }
                                }
                            }
                        } catch (e) {
                            console.warn(this.LOG_STRING, 'Failed to create legend text for chart type different than line: ' + e)
                        }
                    }

                    return text;
                },
                customizeHint: function () {
                    if ($scope.isLineChart)
                        return this.seriesName.replace('undefined: ', '')
                },

            },
            onLegendClick: e => {
                this.onLegendClick = true
                var currentSeries: DevExpress.viz.baseSeriesObject = e.component.getSeriesByName(e.target.name);
                // TODO: why currentSeries is null in pie chart
                // TODO: @Upper - bcoz e.target is string for pie chart (probably bcoz pie chart has only 1 series)
                if (currentSeries) {
                    var visibleType: VisibleType
                    this.visibleTypes.some(vt => {
                        if (vt.id == currentSeries.tag["typeId"]) {
                            visibleType = vt
                            return true
                        }
                        return false
                    })
                    if (visibleType) {
                        if (currentSeries.isVisible()) {
                            visibleType.state = false
                            currentSeries.hide()
                        } else {
                            visibleType.state = true
                            currentSeries.show()
                        }
                    }

                    if (onLegendClickTimeout) {
                        clearTimeout(onLegendClickTimeout)
                    }

                    onLegendClickTimeout = setTimeout(() => {
                        this.updateValueAxes(true)
                        var dataTypesId = this.visibleTypes.filter(type => type.state).map(type => type['dataTypeId'])
                        var uniq = _.uniq(dataTypesId)
                        OperatorLayoutSettings.setSetting($scope.source.dataTypesChartStorageName, uniq)
                    }, 500)

                } else if ((e as any).points) {
                    const points: DevExpress.viz.piePointObject[] = (e as any).points
                    if (points && points.length > 0) {
                        for (const pt of points) {
                            if (pt.isVisible()) {
                                pt.hide()
                            } else {
                                pt.show()
                            }
                        }

                        if (onLegendClickTimeout) {
                            clearTimeout(onLegendClickTimeout)
                        }

                        onLegendClickTimeout = setTimeout(() => {
                            this.updateValueAxes(true)
                        }, 500)
                    }
                }
            },
            animation: {
                enabled: false
            }
        }

        if (!$scope.source.isKPI) {
            $scope.chartOptions.argumentAxis.label = {
                customizeText: (options) => {
                    return moment(options.value).format(this.dateTimeFormat)
                }
            }
        }

        $scope.tooltipButtonOptions = {
            icon: 'tips',
            hint: i18n("Common", "Tooltip"),
            onInitialized: e => {
                this.tooltipButton = e.component
            },
            onClick: (e) => {
                $scope.areTooltipsShared = !$scope.areTooltipsShared
                if ($scope.areTooltipsShared) {
                    e.element.addClass('dx-state-focused')
                } else {
                    e.element.removeClass('dx-state-focused')
                }
                OperatorLayoutSettings.setSetting(OperatorLayoutSettings.SharedTooltips, $scope.areTooltipsShared)
                this.chart.option('tooltip.shared', $scope.areTooltipsShared)
            }
        }

        $scope.legendButtonOptions = {
            template: TemplateHelper.getIcon(ConfigHelper.Icon.LEGEND, this.userSession),
            hint: i18n('Common', 'Legend'),
            onInitialized: e => {
                this.legendButton = e.component
            },
            onClick: (e) => {
                $scope.isLegendVisible = !$scope.isLegendVisible
                if ($scope.isLegendVisible) {
                    e.element.addClass('dx-state-focused')
                } else {
                    e.element.removeClass('dx-state-focused')
                }
                OperatorLayoutSettings.setSetting(OperatorLayoutSettings.IsLegendVisible, $scope.isLegendVisible)
                this.chart.option('legend.visible', $scope.isLegendVisible)
                if (!$scope.isLegendVisible && this.legendError) {
                    $timeout(() => {
                        this.resetChartSize()
                        this.refreshChart()
                    })
                }
            }
        }

        $scope.rotateButtonOptions = {
            template: TemplateHelper.getIcon(ConfigHelper.Icon.ROTATE, this.userSession),
            hint: i18n('Common', 'Rotate'),
            onInitialized: e => {
                this.rotateButton = e.component
            },
            onClick: (e) => {
                $scope.isRotated = !$scope.isRotated
                if ($scope.isRotated) {
                    e.element.addClass('dx-state-focused')
                } else {
                    e.element.removeClass('dx-state-focused')
                }
                OperatorLayoutSettings.setSetting(OperatorLayoutSettings.IsRotated, $scope.isRotated)
            }
        }

        $scope.resizeButtonOptions = {
            template: TemplateHelper.getIcon(ConfigHelper.Icon.FULLSCREEN, this.userSession),
            hint: i18n('Common', 'Fullscreen'),
            onInitialized: e => {
                this.resizeButton = e.component
            },
            onClick: e => {
                this.resizeChart(parent, e)
            }
        }

        $scope.$on(EventsNames.WINDOW_RESIZED, () => {
            $timeout(() => {
                this.resetChartSize()
                this.refreshChart()
            })
        })
    }

    private resetChartSize() {
        if (this.chart) {
            this.legendError = false
            var chartContainer = this.$element.find('.chart-container')
            this.chart.option({
                'height': chartContainer.height(),
                'width': chartContainer.width()
            })
        }
    }

    private resizeChart(parent, e = null) {
        if (e) {
            if (this.$scope.isChartFullscreen) {
                e.element.addClass('dx-state-focused')
            } else {
                e.element.removeClass('dx-state-focused')
            }
        }

        this.$scope.isChartFullscreen = !this.$scope.isChartFullscreen
        if (this.$scope.isChartFullscreen) {
            this.$element.appendTo('body')
        } else {
            this.$element.appendTo(parent)
        }

        this.$timeout(() => {
            this.resetChartSize()
            this.refreshChart()
        })
    }

    private setupButtons(result: any) {
        if (!result || result.length == 0) {
            if (this.legendButton) { this.legendButton.option('disabled', true) }
            if (this.rotateButton) { this.rotateButton.option('disabled', true) }
            if (this.resizeButton) { this.resizeButton.option('disabled', true) }
            if (this.tooltipButton) { this.tooltipButton.option('disabled', true) }
        }
        else {
            if (this.legendButton) { this.legendButton.option('disabled', false) }
            if (this.rotateButton) { this.rotateButton.option('disabled', false) }
            if (this.resizeButton) { this.resizeButton.option('disabled', false) }
            if (this.tooltipButton) { this.tooltipButton.option('disabled', false) }
        }
    }

    private setupTypes(types: MeasurementDataType[], result: any) {
        this.selectedTypes = types
        // restore selected
        var savedVisibleDateTypes = OperatorLayoutSettings.getSetting(this.chartStorageName, [])
        this.visibleTypes = types.map(type => {
            var dataTypeId = type.dataTypeId || type.id
            return {
                id: type.id,
                dataTypeId: dataTypeId,
                state: savedVisibleDateTypes.indexOf(dataTypeId) != -1,
                axisName: type.unitType.toString(),
                seriesName: type.name.replace('undefined: ', '')
            }
        })

        // select existing
        if (result && result.length > 0) {
            var resultIds = result.map(r => { return r.dataType })
            this.visibleTypes = this.visibleTypes.filter(vt =>
                resultIds.some(ri => ri == vt.id)
            )
        }
        else {
            this.visibleTypes = []
        }

        // select first
        if (this.visibleTypes.length > 0 && this.visibleTypes.filter(type => type.state).length == 0) {
            this.visibleTypes = this.visibleTypes.map((type, index) => {
                if (index == 0) type.state = true
                return type
            })
        }
    }

    private getValueAxes(types: MeasurementDataType[], separateTypes: any[]): DevExpress.viz.dxChartValueAxis[] {
        var axes = <DevExpress.viz.dxChartValueAxis[]>[]
        var counter = 0
        var hiddenYAxisDataTypes = this.localDictionary.getHiddenYAxisSync()
        var unitedAxes: number[] = []

        types.forEach((type) => {
            var visible = hiddenYAxisDataTypes.indexOf(type.id) < 0 ? true : false
            if (separateTypes.indexOf(type.id) > -1) {
                axes[type.id] = {
                    name: type.id.toString(),
                    valueType: "numeric",
                    title: {
                        text: hiddenYAxisDataTypes.indexOf(+type.id) < 0 ? type.name.toString() + " " + this.encloseUnit(type.unitName) : null,
                    },
                    position: (counter % 2) != 0 ? "right" : "left",
                    visible: visible,
                    showZero: false,
                    label: {
                        visible: visible
                    }
                }

                this.visibleTypes.some(vt => {
                    if (type.id == vt.id) {
                        vt.axisName = type.id.toString()
                        return true
                    }
                    return false
                })

                counter++
            } else if (!axes[type.unitType]) {
                axes[type.unitType] = {
                    name: type.unitType.toString(),
                    valueType: "numeric",
                    title: {
                        text: hiddenYAxisDataTypes.indexOf(+type.id) < 0 ? this.encloseUnit(type.unitName) : null,
                    },
                    position: (counter % 2) != 0 ? "right" : "left",
                    visible: visible,
                    showZero: false
                }
                if (type['isDate']) {
                    axes[type.unitType].valueType = "string"
                    axes[type.unitType].label = {
                        customizeText: (options) => {
                            return moment(options.value).format(this.dateTimeFormat)
                        }
                    }
                } else {
                    axes[type.unitType].label = {
                        customizeText: (options) => {
                            if (this.isNumeric(options.value)) {
                                return this.dataTypeFormatter.formatDisplayValue(+options.value, {
                                    idDataType: type.dataTypeId,
                                    idDataTypeClass: Enums.DataTypeClass.Integer
                                })
                            }
                            else {
                                return options.value as string
                            }
                        }
                    }
                }

                unitedAxes.push(type.unitType)
                counter++
            }
        })

        this.unitedAxes = {}
        unitedAxes.forEach(ua => {
            var united = types.filter(t => {
                if (separateTypes.indexOf(t.id) < 0) {
                    return true
                }
                return false
            })
            this.unitedAxes[ua] = united.filter(axis => { if (axis.unitType == ua) return true })
            if (united.every(u => { return hiddenYAxisDataTypes.indexOf(u.dataTypeId) > -1 })) {
                axes[ua].label.visible = false
                axes[ua].visible = false
                axes[ua].title = null
                axes[ua].grid = { visible: false }
            }
        })
        axes = axes.filter(a => <any>a)
        this.getMinMaxForAxis(axes, this.unitedAxes)
        return axes
    }

    private setValueAxesTitles(axes: DevExpress.viz.dxChartValueAxis[]): DevExpress.viz.dxChartValueAxis[] {
        var counter = 0
        var hiddenAxisDataType: number[] = this.localDictionary.getHiddenYAxisSync()
        axes.forEach(axis => {
            var selectedType: MeasurementDataType
            this.selectedTypes.some(st => {
                if (st.unitType.toString() == axis.name) {
                    selectedType = st
                    return true
                }
                return false
            })

            if (this.visibleTypes) {
                if (selectedType) {
                    var allSeries: any[] = this.chart['series'] ? this.chart.getAllSeries() : []  //all series in chart
                    var visibleSeries = []  //visible series
                    allSeries.forEach(as => {
                        if (as.isVisible() && as.tag != undefined)
                            visibleSeries.push(as.tag)
                    })
                    if (visibleSeries.every(vs => { return hiddenAxisDataType.indexOf(vs.dataTypeId) > -1 })) {
                        axis.visible = false
                        axis.title = null
                        axis.grid = { visible: false }
                    } else {
                        axis.visible = true
                        axis.title = {
                            text: this.encloseUnit(selectedType.unitName)
                        }
                    }
                } else {
                    var axisTitle = axis.title
                    var vt = this.visibleTypes.some(vt => {
                        if (vt.axisName == axis.name && vt.state) {
                            axis.visible = hiddenAxisDataType.indexOf(+axis.name) < 0 ? true : false //true
                            selectedType = this.selectedTypes.filter(st => axis.name.indexOf(st.id.toString()) >= 0)[0]
                            axis.title = hiddenAxisDataType.indexOf(+axis.name) < 0 ? axisTitle : null
                            return true
                        }
                        else {
                            axis.visible = false
                            axis.title = null
                            axis.grid = { visible: false }

                            return false
                        }
                    })
                }
            }

            if (this.unitedAxes[axis.name]) {
                var allSeries: any[] = this.chart['series'] ? this.chart.getAllSeries() : []
                var hideAxis: boolean = false
                var visibleSeries = []
                allSeries.forEach(as => {
                    if (as.isVisible() && as.tag != undefined)
                        visibleSeries.push(as.tag)
                })
                if (visibleSeries.length > 0) {
                    var visibleSeriesDataType = visibleSeries.map(vs => { return vs.dataTypeId }).filter(id => { return this.unitedAxes[axis.name].map(a => { return a.dataTypeId }).indexOf(id) > -1 }).filter(this.distinct)
                    if (visibleSeriesDataType.length > 0) {
                        if (visibleSeriesDataType.length == 1) {
                            if (hiddenAxisDataType.indexOf(visibleSeriesDataType[0]) > -1) {
                                hideAxis = true
                            }
                        } else {
                            if (visibleSeriesDataType.every(vs => { return hiddenAxisDataType.indexOf(vs) > -1 })) {
                                hideAxis = true
                            } else {
                                axis.visible = true
                                axis.title = {
                                    text: this.encloseUnit(selectedType.unitName)
                                }
                                axis.label.visible = true
                            }
                        }
                    } else {
                        hideAxis = true
                    }
                }
                else {
                    hideAxis = true
                }
                if (hideAxis) {
                    axis.label.visible = false
                    axis.visible = false
                    axis.title = null
                    axis.grid = { visible: false }
                }
            }
        })

        var needGrid: boolean = true
        axes.forEach(axis => {
            if (needGrid && axis.visible) {
                axis.grid = { visible: true }
                needGrid = false
            }
            else axis.grid = { visible: false }
        })

        return axes
    }

    private encloseUnit(unit) {
        return unit.trim() ? "[" + unit + "]" : ""
    }

    private updateValueAxes = _.debounce((onlyTitles: boolean = false) => {

        this.localDictionary.getSeparateYAxis().then(sepAxis => {
            this.chart.option("valueAxis", this.setValueAxesTitles(this.getValueAxes(this.selectedTypes, sepAxis)))

            if (this.chart['series'])
                this.chart.getAllSeries().forEach(series => {
                    if (this.visibleTypes) {
                        this.visibleTypes.some(vt => {
                            if (vt.seriesName == series.name) {
                                if (vt.state != series.isVisible()) {
                                    vt.state ? series.show() : series.hide()
                                }
                                return true
                            }
                            return false
                        })
                    }
                })

            this.updateChartSeries(this.selectedTypes)

            if (onlyTitles) {
                this.chart.render()
            }
        })
    }, 100)

    private updateChartSeries = _.debounce((selectTypes: MeasurementDataType[]) => {
        selectTypes = selectTypes.filter(st => this.visibleTypes.some(vt => vt.id == st.id))

        var axes = this.chart.option("valueAxis")

        this.$q.all(selectTypes.map(type => {
            return this.localDictionary.getSeriesColor(type.dataTypeId).then(seriesColor => {
                return { id: type.id, color: seriesColor.color, isForced: seriesColor.isForced }
            })
        })).then(colors => {
            this.colors = colors
            let reservedColors = [];

            let series = [];
            if (this.$scope.isLineChart) {
                series = selectTypes.map((type, index) => {
                    const isVisible: boolean = this.showAllSeries || (this.visibleTypes && this.visibleTypes.some(vt => vt.id == type.id && vt.state))
                    const customAxes: any = axes.filter(a => {
                        if (a.name != null) {
                            a.name.indexOf(type.id.toString()) >= 0
                        }

                        return false
                    })
                    const seriesColor: any = this.colors.filter(color => color.id == type.id)[0]
                    const color: any = this.selectColor(reservedColors, index, type)

                    reservedColors.push({
                        color: color,
                        dataTypeId: type.dataTypeId,
                        isForced: !seriesColor || seriesColor.isForced
                    });

                    return {
                        name: type.name,
                        valueField: type.name,
                        tag: <any>{
                            unitType: type.unitType,
                            unitName: type.unitName,
                            typeId: type.id,
                            isDate: type['isDate'],
                            dataTypeId: type.dataTypeId
                        },
                        type: type['chartType'],
                        visible: isVisible,
                        axis: customAxes.length > 0 ? customAxes[0].name : type.unitType.toString(),
                        color: color
                    }
                });
            }
            else {
                if (ConfigHelper.getValue<boolean>(Enums.SimaxConfigSection.Charts, ConfigHelper.Key.IsPieChartSeriesColoringEnabled) === true) {
                    const palette: string[] = selectTypes.map((type: MeasurementDataType, index: number) => {
                        return this.selectColor(reservedColors, index, type)
                    })

                    this.chart.option("palette", palette)
                }

                let chartTypeFromItem: any = null
                for (const item of selectTypes) {
                    if (chartTypeFromItem === null) {
                        chartTypeFromItem = item.chartType
                    } else if (chartTypeFromItem != item.chartType) {
                        chartTypeFromItem = null
                        break
                    }
                }

                if (chartTypeFromItem != null) {
                    this.chart.option('type', chartTypeFromItem)
                }

                series = [{
                    valueField: 'value',
                    argumentField: 'dataType',
                    label: {
                        visible: true,
                        connector: { visible: true },
                        customizeText: (str) => {
                            const pointType = this.selectedTypes.filter(type => type.id == str.argument)
                            return `${str.point.tag}`
                        },
                        font: {
                            color: "#000",
                            size: 16
                        }
                    }
                }];
            }

            this.chart.option({ series })
        })
    }, 100)

    private updateValueAxisWidth = (e) => {
        var valueAxis = this.chart.option("valueAxis")
        if (e.target) {
            valueAxis.forEach((axis) => {
                if (axis.name == e.target.axis) {
                    var color = this.colors.filter(color => color.id == e.target.tag.typeId).length ? this.colors.filter(color => color.id == e.target.tag.typeId)[0].color : ''
                    axis.color = color ? color : '#000000'
                    axis.width = 3
                }
                else {
                    axis.color = '#d3d3d3'
                    axis.width = 1
                }
            })
            this.$timeout(() => {
                this.chart.option("valueAxis", valueAxis)
            })
        }
    }

    private updateChart(data: MeasurementModel[] = [], selectedTypes, filterId, tickInterval, groups: any[], devices: any[], meters: any[], labelFormat, chartTypesStorage?: any) {
        this.chartStorageName = chartTypesStorage
        this.chart.beginUpdate()
        this.updateValueAxes()

        this.setupTypes(selectedTypes, data)
        this.updateValueAxes()

        var points = []
        this.data = []

        data.forEach((record: any) => {
            var id = null
            switch (filterId) {
                case 1:
                    id = record.idLocation
                    break
                case 2:
                    id = record.serialNbr
                    break
                case 3:
                    id = record.idMeter
                    break
            }

            const pointType = selectedTypes.find(type => {
                // return (type.dataTypeId == record.dataTypeId) || (type.dataTypeId == record.dataType)
                return (type.id == record.dataType)
            })
            const pointName = this.$scope.isLineChart ? record.date + ' ' + id : pointType.name;

            if (!points[pointName]) {
                var info = ''
                switch (filterId) {
                    case 1:
                        var title = record.idLocation
                        if (groups && groups.length > 0) title = groups.filter(g => g[CommonHelper.idField] == record.idLocation).map(g => g.name)[0]
                        if (!title) title = record.idLocation
                        if (title && title !== null) info = i18n('Common', 'Location') + ': ' + title;
                        break
                    case 2:
                        var title = record.serialNbr
                        if (devices && devices.length > 0) title = devices.filter(d => d[CommonHelper.idField] == record.serialNbr).map(d => d.name)[0]
                        if (!title) title = record.serialNbr
                        if (title && title !== null) info = i18n('Common', 'Device') + ': ' + title;
                        break
                    case 3:
                        var title = record.idMeter
                        if (meters && meters.length > 0) title = meters.filter(m => m[CommonHelper.idField] == record.idMeter).map(m => m.name)[0]
                        if (!title) title = record.idMeter
                        if (title && title !== null) info = i18n('Common', 'Meter') + ': ' + title;
                        break
                }

                points[pointName] = {
                    date: new Date(record.date),
                    info: info,
                    value: record.value,
                    dataType: record.dataType,
                    color: pointType && pointType.color ? pointType.color : `#${Math.floor(Math.random() * 10)}${Math.floor(Math.random() * 10)}${Math.floor(Math.random() * 10)}`,
                    name: pointName
                }
            }
            var field
            if (selectedTypes.some(value => {
                if (value.id == record.dataType) {
                    field = value.name
                    return true
                }
                return false
            })) {
                points[pointName][field] = record.value
            }
        })

        for (var i in points) {
            this.data.push(points[i])
        }

        this.updateChartSeries(selectedTypes)
        this.$timeout(() => {
            this.updateValueAxes()
            this.updateAggregation()

            this.chart.option({
                argumentAxis: {
                    tickInterval: tickInterval,
                    label: labelFormat,
                },
                dataSource: this.data
            })

            this.toggleAggregation();

            try {
                this.chart.render({ animate: false })
                this.chart.endUpdate()
            } catch (e) {
            }

            this.setupButtons(data)
        }, 500)
    }

    private refreshChart() {
        if (this.chart) {
            try {
                this.chart.render()
            } catch (e) {
            }
        }
    }

    private isNumeric(n) {
        return !isNaN(parseFloat(n)) && isFinite(n);
    }

    private distinct(value, index, self) {
        return self.indexOf(value) === index;
    }

    private getMinMaxForAxis(axes: DevExpress.viz.charts.dxChartValueAxis[], unitedAxis: any) {
        var dataTypesFormatGroup: DataTypeFormatGroupModel[] = <DataTypeFormatGroupModel[]>CommonHelper.getValueFromLocalStorage('dataTypeFormatGroup') || [] //defined dataTypesFormats in dataTypesFormatGroups        
        var allSeries: any[] = this.chart['series'] ? this.chart.getAllSeries() : []  //all series in chart
        var visibleSeries = []  //visible series        
        allSeries.forEach(as => {
            if (as.isVisible() && as.tag != undefined) visibleSeries.push(as.tag)
        })
        if (visibleSeries.length > 0) {
            axes.forEach(axis => {
                var unitedDataTypes: MeasurementDataType[] = unitedAxis[axis.name]
                var minRange: number[] = []
                var maxRange: number[] = []
                if (unitedDataTypes)
                    unitedDataTypes.forEach(udt => {
                        if (visibleSeries.filter(vs => { return vs.dataTypeId == udt.dataTypeId }).length > 0) {
                            var dtFormat = this.localDictionary.getDataTypesSync([udt.dataTypeId])[0]
                            var dtfgFormat = (dataTypesFormatGroup.filter(item => { return item.idDataType == udt.dataTypeId }))[0]
                            if (dtfgFormat) {
                                if (dtfgFormat.dataTypeFormatOut.numberMinValue != null && dtfgFormat.dataTypeFormatOut.numberMaxValue != null) {
                                    minRange.push(dtfgFormat.dataTypeFormatOut.numberMinValue)
                                    maxRange.push(dtfgFormat.dataTypeFormatOut.numberMaxValue)
                                }
                            } else {
                                if (dtFormat.format.numberMinValue != null && dtFormat.format.numberMaxValue != null) {
                                    minRange.push(dtFormat.format.numberMinValue)
                                    maxRange.push(dtFormat.format.numberMaxValue)
                                }
                            }

                            if (minRange.length != 0 && maxRange.length != 0) {
                                axis.min = Math.min(...minRange)
                                axis.max = Math.max(...maxRange)
                                axis.valueMarginsEnabled = false
                                //axis.tickInterval = (axis.max - axis.min)/10
                            }
                        }
                    })
                else {
                    if (visibleSeries.filter(vs => { return vs.dataTypeId == axis.name }).length > 0) {
                        var dtfgFormat = (dataTypesFormatGroup.filter(item => { return item.idDataType == +axis.name }))[0]
                        if (dtfgFormat) {
                            if (dtfgFormat.dataTypeFormatOut.numberMinValue != null && dtfgFormat.dataTypeFormatOut.numberMaxValue != null) {
                                axis.min = dtfgFormat.dataTypeFormatOut.numberMinValue
                                axis.max = dtfgFormat.dataTypeFormatOut.numberMaxValue
                                axis.valueMarginsEnabled = false
                            }
                        }
                    }
                }
            })
        }
    }

    private selectColor(existingColors: any[], index: number, type: MeasurementDataType): any {
        const seriesColor: any = this.colors.filter(color => color.id == type.id)[0]
        let color: any = null

        if (seriesColor) {
            color = seriesColor.color;
        }

        if (!color) {
            this.colors.forEach(color => {
                if (color.id === type.id) {
                    if (ChartDirectiveController.AlternativeColors[index]) {
                        color.color = ChartDirectiveController.AlternativeColors[index];
                    }
                }
            })
        }

        const colorOccurs = existingColors.filter(rColor => {
            return rColor.color == color
                && rColor.dataTypeId == type.dataTypeId
                && (!seriesColor || !seriesColor.isForced)
        })

        if (colorOccurs.length != 0) {
            color = ChartDirectiveController.AlternativeColors[existingColors.length - 1] ? ChartDirectiveController.AlternativeColors[existingColors.length - 1] : `#${Math.floor(Math.random() * 10)}${Math.floor(Math.random() * 10)}${Math.floor(Math.random() * 10)}`;
        }

        return color
    }


    //#region Aggregation

    private isAggregationEnabled: boolean = false;

    private isAggregationActive(): boolean {
        return this.chart.option("commonSeriesSettings.aggregation.enabled") || false;
    }

    private toggleAggregation(): void {
        this.chart.option("commonSeriesSettings.aggregation.enabled", this.isAggregationEnabled);
    }

    private lastVisualRangeDifference: moment.Duration = null;
    private updateAggregationIntervalsDebounced = _.debounce((visualRange: VizRange) => {
        if (this.isAggregationEnabled) {
            this.chart.beginUpdate();
            this.updateAggregationIntervalsByVisualRange(visualRange);
            this.chart.endUpdate();
        }
    }, 200);

    private updateAggregation(): void {
        if (this.isAggregationEnabled) {
            const sortedData = _.sortBy(this.data, (data: any) => {
                return data.date.getTime();
            });

            const fromDate = _.first(sortedData);
            const lastDate = _.last(sortedData);

            if (fromDate != null && lastDate != null) {
                this.updateAggregationIntervalsByVisualRange({
                    startValue: fromDate.date,
                    endValue: lastDate.date
                });
            }
        }
    }

    private updateAggregationIntervalsByVisualRange(visualRange: VizRange): void {
        const currentVisualRange: VizRange = this.chart.getArgumentAxis().visualRange();

        if (moment.isDate(visualRange.startValue) && moment.isDate(visualRange.endValue)) {
            const difference: moment.Duration = moment.duration(moment(visualRange.endValue).diff(moment(visualRange.startValue)));

            if (this.lastVisualRangeDifference == null || this.lastVisualRangeDifference.asMilliseconds() !== difference.asMilliseconds()) {
                this.lastVisualRangeDifference = difference

                const days = difference.asDays() || 0;
                const weeks = difference.asWeeks() || 0;
                const months = difference.asMonths() || 0;
                const years = difference.asYears() || 0;

                if (days < 2) {
                    this.chart.option("argumentAxis", { aggregationInterval: null });
                    this.chart.option("commonSeriesSettings.aggregation", {
                        enabled: false,
                    });
                }
                else if (days < 4) {
                    this.chart.option("argumentAxis", { aggregationInterval: { hours: 3 } });
                    this.chart.option("commonSeriesSettings.aggregation", {
                        enabled: true,
                    });
                }
                else if (weeks < 1) {
                    this.chart.option("argumentAxis", { aggregationInterval: { hours: 6 } });
                    this.chart.option("commonSeriesSettings.aggregation", {
                        enabled: true,
                    });
                }
                else if (weeks < 2) {
                    this.chart.option("argumentAxis", { aggregationInterval: { hours: 12 } });
                    this.chart.option("commonSeriesSettings.aggregation", {
                        enabled: true,
                    });
                }
                else if (months < 1) {
                    this.chart.option("argumentAxis", { aggregationInterval: { hours: 24 } });
                    this.chart.option("commonSeriesSettings.aggregation", {
                        enabled: true,
                    });
                }
                else if (months < 2) {
                    this.chart.option("argumentAxis", { aggregationInterval: { hours: 48 } });
                    this.chart.option("commonSeriesSettings.aggregation", {
                        enabled: true,
                    });
                }
                else if (months < 4) {
                    this.chart.option("argumentAxis", { aggregationInterval: { hours: 96 } });
                    this.chart.option("commonSeriesSettings.aggregation", {
                        enabled: true,
                    });
                }
                else if (months < 6) {
                    this.chart.option("argumentAxis", { aggregationInterval: "week" });
                    this.chart.option("commonSeriesSettings.aggregation", {
                        enabled: true,
                    });
                }
                else if (years < 5) {
                    this.chart.option("argumentAxis", { aggregationInterval: "month" });
                    this.chart.option("commonSeriesSettings.aggregation", {
                        enabled: true,
                    });
                }
                else {
                    this.chart.option("argumentAxis", { aggregationInterval: "year" });
                    this.chart.option("commonSeriesSettings.aggregation", {
                        enabled: true,
                    });
                }

                this.chart.getArgumentAxis().visualRange(currentVisualRange);
            }
        }
    }

    //#endregion
}